import { CLAIM_FORM_PENDING_PREFIX, toPendingCards } from "./pendingPolicies";

/**
 * Pure helpers for the wizard's "Upload as File" path — a claim form read by
 * `POST /clinics/claim-form/read/` (estimateClaimsApi.readClaimForm). The read
 * names the patient, their plan(s) and the submission; these decide how each
 * of those lands in the wizard. No React here, so the rules are tested directly.
 */

/** The form's patient as NewPatientForm's starting values. */
export function claimFormPatientPrefill(patient) {
  const p = patient || {};
  return {
    firstName: p.first_name || "",
    lastName: p.last_name || "",
    birthDate: p.birth_date || "",
    phone: p.phone || "",
    address: p.address || "",
    city: p.city || "",
    state: p.state || "",
    zipCode: p.zip_code || "",
  };
}

/** The one patient to select straight away: a single exact match (both names
 *  and the date of birth — the rule the PMS sync links by). Otherwise null, and
 *  the office chooses. */
export function exactMatch(candidates) {
  const exact = (candidates || []).filter(function (c) { return c.match === "exact"; });
  return exact.length === 1 ? exact[0] : null;
}

function liveIds(policies) {
  const ids = {};
  (policies || []).forEach(function (p) { ids[p.id] = true; });
  return ids;
}

/** Is this plan one the patient already has (and that the wizard is listing)? */
function matchedId(preview, live) {
  const id = preview && preview.existing_local_policy_id;
  return id != null && live[id] ? id : null;
}

/**
 * Plans on the form the patient doesn't have yet — pending cards ("From the
 * claim form · not saved yet"); approving one creates the policy.
 *
 * A plan matched to a listed policy is NOT a card: that policy is the plan, and
 * the form's values become suggestions on it (claimFormSuggestions).
 */
export function claimFormCards(previews, policies) {
  const live = liveIds(policies);
  return toPendingCards({
    policies: (previews || []).filter(function (p) { return !matchedId(p, live); }),
  });
}

/** `{ [policyId]: preview }` for plans the patient already has, so the review
 *  shows "Claim form: X · Use" beside each field that differs. */
export function claimFormSuggestions(previews, policies) {
  const live = liveIds(policies);
  const out = {};
  (previews || []).forEach(function (p) {
    const id = matchedId(p, live);
    if (id != null) out[id] = p;
  });
  return out;
}

/** What to pre-select: the FIRST payor — its existing policy, else its card.
 *  The second payor is offered, not chosen for them. */
export function claimFormPreselection(previews, policies) {
  const first = (previews || [])[0];
  if (!first) return null;
  const id = matchedId(first, liveIds(policies));
  return id != null ? id : CLAIM_FORM_PENDING_PREFIX + first.insurance_type;
}

/**
 * The submission each selected policy carries when the form was uploaded — what
 * the estimates step would have collected, read off the form instead.
 *
 * The stored form goes with every one: the server waives the typed fields when
 * a stored form is attached, and the team reads anything we couldn't from the
 * form itself. Same shape `serializeEstimates` sends.
 */
export function estimatesFromClaimForm(claimForm, policyIds) {
  if (!claimForm || !claimForm.claim_form_path) return {};
  const c = claimForm.claim || {};
  const row = {
    amount: c.amount || null,
    service_date: c.service_date || null,
    patient_name: c.patient_name || null,
    provider_name_raw: c.provider_name_raw || null,
    provider_id: c.provider_id || null,
    cda_itrans_number: c.cda_itrans_number || null,
    procedures: (c.procedures || []).filter(function (p) { return p && p.code; }),
    claim_form_path: claimForm.claim_form_path,
  };
  const out = {};
  (policyIds || []).forEach(function (id) {
    out[String(id)] = [Object.assign({}, row)];
  });
  return out;
}

function money(amount) {
  const n = Number(amount);
  if (!amount || !isFinite(n)) return "";
  return "$" + n.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * One line saying what goes with the request: "Predetermination · $1,986.00 ·
 * 3 procedures · Dr. Jane Example". Empty parts are left out.
 */
export function describeClaimForm(claimForm) {
  if (!claimForm) return "";
  const c = claimForm.claim || {};
  const procedures = (c.procedures || []).length;
  const kind = claimForm.document_kind === "predetermination"
    ? "Predetermination"
    : claimForm.document_kind === "claim" ? "Claim" : "";
  return [
    kind,
    money(c.amount),
    procedures ? procedures + (procedures === 1 ? " procedure" : " procedures") : "",
    c.provider_name_raw || "",
  ].filter(Boolean).join(" · ");
}
